import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Field, InputGroup, Select, RemoveButton } from 'core/form';
import { PopoverInteractionKind } from '@blueprintjs/core';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';

import { Table } from 'core/components/table';
import CELL_TYPES from 'core/components/table/CELL_TYPES';
import CredentialDialog from 'app/views/settings/credentials/CredentialDialog';
import CloudExportGcpCustomAppServiceAccountCollection from 'app/stores/clouds/gcp/CloudExportGcpServiceAccountCollection';
import { Button, Box, Heading, ButtonGroupTab, ButtonGroupTabs, Card, Popover, Flex, Text } from 'core/components';

const TAB_CUSTOM_SERVICE_ACCOUNT = 'service_account';
const TAB_CUSTOM_KENTIK_SERVICE_ACCOUNT = 'kentik_service_account';

@withRouter
@inject('$cloudExportWizard', '$credentials')
@observer
class GcpAuthenticationPanel extends Component {
  gcpServiceAccountCollection = new CloudExportGcpCustomAppServiceAccountCollection();

  state = {
    isLoading: false,
    popoverIsOpen: false,
    credentialModel: null,
    showAddCredentialDialog: false,
    isCredentialsOptionsLoading: true,
    isEditingPrevServiceAccountCredentialSecret: false
  };

  async componentDidMount() {
    const { $credentials, $cloudExportWizard, form } = this.props;

    $credentials.groupedCollection.fetch().then(() => {
      this.setState({ isCredentialsOptionsLoading: false });
    });

    if (form) {
      $cloudExportWizard.setForm(form);
    }

    const selectedKentikGcpServiceAccountId = $cloudExportWizard.getFieldValue(
      'properties.kentik_gcp_service_account_id'
    );

    if (selectedKentikGcpServiceAccountId) {
      // load and set current exporter gcp service account record
      this.gcpServiceAccountCollection.fetch({ force: true }).then(() => {
        this.handleSelectPreviousServiceAccountRecord(
          this.gcpServiceAccountCollection.find((model) => model.get('id') === selectedKentikGcpServiceAccountId)
        );
      });
    }
  }

  handleTabChange = (tabId) => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.gcp_api_access_method', tabId);
  };

  handleSelectPreviousServiceAccountRecord = (model) => {
    const { $cloudExportWizard } = this.props;

    if (!model) {
      return;
    }

    $cloudExportWizard.setFieldValue('properties.kentik_gcp_service_account_id', model.get('id'));
    $cloudExportWizard.setFieldValue('properties.gcp_service_account_email', model.get('gcp_service_account_email'));
    $cloudExportWizard.setFieldValue(
      'properties.kentik_credential_secret_name',
      model.get('metadata.mn_credential_vault_name')
    );

    this.setState({ popoverIsOpen: false, isEditingPrevServiceAccountCredentialSecret: false });
  };

  handleDeletePreviousServiceAccountRecord = async (model) => {
    if (!model) {
      return;
    }

    this.setState({ isLoading: true });

    await model.destroyModel();

    this.setState({ isLoading: false });
  };

  handleEditPreviousServiceAccountCredentialSecret = (model) => {
    const { $cloudExportWizard } = this.props;

    if (!model) {
      return;
    }

    // Keep the saved service account selected, but allow editing only the secret name.
    $cloudExportWizard.setFieldValue('properties.kentik_gcp_service_account_id', model.get('id'));
    $cloudExportWizard.setFieldValue('properties.gcp_service_account_email', model.get('gcp_service_account_email'));
    $cloudExportWizard.setFieldValue(
      'properties.kentik_credential_secret_name',
      model.get('metadata.mn_credential_vault_name')
    );

    this.setState({ popoverIsOpen: false, isEditingPrevServiceAccountCredentialSecret: true });
  };

  handleCreateNewServiceAccountRecord = () => {
    const { $cloudExportWizard } = this.props;

    $cloudExportWizard.setFieldValue('properties.kentik_gcp_service_account_id', null);
    $cloudExportWizard.setFieldValue('properties.gcp_service_account_email', '');
    $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', '');

    this.setState({ isEditingPrevServiceAccountCredentialSecret: false });
  };

  handleSaveEditedCredentialSecret = async () => {
    const { $cloudExportWizard } = this.props;

    this.setState({ isLoading: true });

    const gcpServiceAccountModel = this.gcpServiceAccountCollection.find(
      (model) => $cloudExportWizard.getFieldValue('properties.kentik_gcp_service_account_id') === model.get('id')
    );

    if (!gcpServiceAccountModel) {
      console.error('Unable to find gcp service account model to update credential secret');
      this.setState({ isEditingPrevServiceAccountCredentialSecret: false, isLoading: false });
      return;
    }

    gcpServiceAccountModel.set(
      'mn_credential_vault_name',
      $cloudExportWizard.getFieldValue('properties.kentik_credential_secret_name')
    );

    try {
      await gcpServiceAccountModel.save();
    } catch (error) {
      console.error('Unable to update gcp service account secret', error);
    }

    this.setState({ isEditingPrevServiceAccountCredentialSecret: false, isLoading: false });
  };

  handleLoadPreviousServiceAccountRecords = async () => {
    this.setState((prevState) => ({ popoverIsOpen: !prevState.popoverIsOpen }));
    await this.gcpServiceAccountCollection.fetch({ force: true });
  };

  handleSaveNewServiceAccountRecord = async () => {
    const { $cloudExportWizard } = this.props;

    this.setState({ isLoading: true });

    const gcpServiceAccountModel = this.gcpServiceAccountCollection.forge({
      gcp_service_account_email: $cloudExportWizard.getFieldValue('properties.gcp_service_account_email'),
      mn_credential_vault_name: $cloudExportWizard.getFieldValue('properties.kentik_credential_secret_name')
    });

    try {
      await gcpServiceAccountModel.save();
      $cloudExportWizard.setFieldValue('properties.kentik_gcp_service_account_id', gcpServiceAccountModel.get('id'));
    } catch (error) {
      console.error('Unable to save gcp service account record', error);
    }

    this.setState({ isLoading: false });
  };

  get previousServiceAccountColumns() {
    return [
      {
        label: 'Service Account Email',
        name: 'gcp_service_account_email',
        ellipsis: false
      },
      {
        label: 'Kentik Credential Name',
        name: 'metadata',
        ellipsis: true,
        renderer: ({ value }) => (
          <Flex alignItems="center" justifyContent="space-between" gap={1} width="100%">
            <Text>{value?.mn_credential_vault_name}</Text>
          </Flex>
        )
      },
      {
        name: 'actions',
        width: 70,
        type: CELL_TYPES.ACTION,
        actions: [
          (model) => (
            <Button
              key="edit"
              icon="edit"
              ariaLabel="Edit"
              minimal
              small
              onClick={(e) => {
                e.stopPropagation();
                this.handleEditPreviousServiceAccountCredentialSecret(model);
              }}
            />
          ),
          (model) => (
            <RemoveButton
              icon="trash"
              minimal
              small
              showIcon
              hideText
              key="remove"
              entityName="Service Account"
              ariaLabel="Remove"
              model={model}
              popoverProps={{
                confirmBodyContent: 'Remove Previous GCP Service Account',
                confirmText: 'Are you sure you want to delete this Service Account?'
              }}
              onRemove={() => this.handleDeletePreviousServiceAccountRecord(model)}
            />
          )
        ]
      }
    ];
  }

  handleCloseCredentialsDialog = (formValues) => {
    const { $cloudExportWizard } = this.props;
    this.setState({ showAddCredentialDialog: false });

    if (formValues && formValues.name && formValues.tokens && formValues.tokens.length > 0) {
      const { name, tokens } = formValues;
      $cloudExportWizard.setFieldValue('properties.kentik_credential_secret_name', `${name}.${tokens[0].key}`);
    }
  };

  handleClickAddCredentialDialog = () => {
    const { $credentials } = this.props;

    this.setState({
      showAddCredentialDialog: true,
      credentialModel: $credentials.groupedCollection.forge({ type: CREDENTIAL_TYPES.cloud })
    });
  };

  render() {
    const { $cloudExportWizard, $credentials } = this.props;
    const { isLoading, popoverIsOpen, showAddCredentialDialog, credentialModel, isCredentialsOptionsLoading } =
      this.state;

    const { isEditingPrevServiceAccountCredentialSecret } = this.state;

    const isPrevServiceAccountSelected = $cloudExportWizard.getFieldValue('properties.kentik_gcp_service_account_id');

    const allowSave =
      !isPrevServiceAccountSelected &&
      $cloudExportWizard.getField('properties.gcp_service_account_email')?.valid &&
      $cloudExportWizard.getField('properties.kentik_credential_secret_name')?.valid;

    const allowSecretSave =
      isEditingPrevServiceAccountCredentialSecret &&
      $cloudExportWizard.getField('properties.kentik_credential_secret_name')?.valid;

    return (
      <Flex flex={1} flexDirection="column" gap="1">
        <ButtonGroupTabs
          omitTabsWrapper
          onChange={this.handleTabChange}
          selectedTabId={
            $cloudExportWizard.getFieldValue('properties.gcp_api_access_method') || TAB_CUSTOM_KENTIK_SERVICE_ACCOUNT
          }
        >
          <ButtonGroupTab
            id={TAB_CUSTOM_KENTIK_SERVICE_ACCOUNT}
            text="Kentik Service Account"
            panel={<Text small>Kentik subscription id will be used for API requests.</Text>}
          />
          <ButtonGroupTab
            id={TAB_CUSTOM_SERVICE_ACCOUNT}
            title="Custom service account"
            panel={
              <Card bg="backgrounds.app">
                <Flex flexDirection="column" gap={1} p={2}>
                  <Flex justifyContent="space-between">
                    <Text as="div" fontWeight="bold" mb={1}>
                      Details
                    </Text>

                    <Flex gap={1}>
                      {isPrevServiceAccountSelected && (
                        <Button small text="Create new" onClick={this.handleCreateNewServiceAccountRecord} />
                      )}
                      <Popover
                        usePortal
                        isOpen={popoverIsOpen}
                        position="right"
                        interactionKind={PopoverInteractionKind.CLICK}
                        minimal={false}
                        modifiers={{
                          flip: { enabled: false },
                          preventOverflow: { enabled: true, boundariesElement: 'window' }
                        }}
                        content={
                          <Box flex={1} p={2} minWidth={500}>
                            <Heading level={4}>GCP Service Accounts</Heading>
                            <Flex flexDirection="column" border="thin">
                              <Table
                                collection={this.gcpServiceAccountCollection}
                                columns={this.previousServiceAccountColumns}
                                onRowClick={(model, e) => {
                                  e.stopPropagation();
                                  this.handleSelectPreviousServiceAccountRecord(model);
                                }}
                              />
                            </Flex>
                          </Box>
                        }
                        target={
                          <Button
                            small
                            icon="archive"
                            text="Saved Service Accounts"
                            onClick={this.handleLoadPreviousServiceAccountRecords}
                          />
                        }
                      />
                    </Flex>
                  </Flex>

                  <Field
                    name="properties.gcp_service_account_email"
                    width="100%"
                    disabled={isPrevServiceAccountSelected}
                    large
                  >
                    <InputGroup />
                  </Field>

                  {!isPrevServiceAccountSelected && (
                    <Flex justifyContent="space-between" gap={6}>
                      <Field
                        flex="1"
                        name="properties.kentik_credential_secret_name"
                        options={$credentials.getCredentialTokenOptionsByTypes([CREDENTIAL_TYPES.cloud])}
                        large
                      >
                        <Select loading={isCredentialsOptionsLoading} menuWidth="100%" />
                      </Field>
                      <Flex flexDirection="column" alignItems="flex-end">
                        <Button
                          onClick={this.handleClickAddCredentialDialog}
                          text="Create New Secret"
                          mt="auto"
                          mb={1}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {isPrevServiceAccountSelected && isEditingPrevServiceAccountCredentialSecret && (
                    <Flex justifyContent="space-between" gap={6} alignItems="center">
                      <Field
                        flex="1"
                        maxWidth="150px"
                        name="properties.kentik_credential_secret_name"
                        options={$credentials.getCredentialTokenOptionsByTypes([CREDENTIAL_TYPES.cloud])}
                        large
                      >
                        <Select loading={isCredentialsOptionsLoading} menuWidth="100%" />
                      </Field>
                      <Flex flexDirection="column" alignItems="flex-end">
                        <Button
                          onClick={this.handleSaveEditedCredentialSecret}
                          text="Update Kentik Credential"
                          loading={isLoading}
                          disabled={!allowSecretSave}
                        />
                      </Flex>
                    </Flex>
                  )}

                  {credentialModel && (
                    <CredentialDialog
                      model={credentialModel}
                      isOpen={showAddCredentialDialog}
                      onClose={this.handleCloseCredentialsDialog}
                      hideTypeSelector
                    />
                  )}

                  {!isPrevServiceAccountSelected && (
                    <Flex justifyContent="flex-end">
                      <Button
                        disabled={!allowSave}
                        loading={isLoading}
                        onClick={this.handleSaveNewServiceAccountRecord}
                        text="Save Service Account"
                      />
                    </Flex>
                  )}
                </Flex>
              </Card>
            }
          />
        </ButtonGroupTabs>
      </Flex>
    );
  }
}

export default GcpAuthenticationPanel;
